import React, { useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import { ReactComponent as RecentIcon } from "../../assets/recent.svg";
import { ReactComponent as HomeIcon } from "../../assets/home.svg";
import { ReactComponent as BellNotiIcon } from "../../assets/bell_noti.svg";
import { ReactComponent as BellIcon } from "../../assets/bell.svg";
import { ReactComponent as ArrowDownIcon } from "../../assets/arrow_down.svg";
import { ReactComponent as LogoutIcon } from "../../assets/logout.svg";
import { ReactComponent as CalendarIcon } from "../../assets/calendar.svg";
import { ReactComponent as ProfileIcon } from "../../assets/user.svg";
import { ReactComponent as PenIcon } from "../../assets/pen.svg";
import { useNavigate, useLocation } from "react-router-dom";
import logo from "../../assets/logo.png";
import { useAppDispatch, useAppSelector } from "../../store";
import defaultAvatar from "../../assets/default_avatar.png";
import { Dropdown, Tooltip } from "antd";
import { logout } from "../../store/actions/auth.action";
import { useSearch } from "../../context/SearchContext";
import Avatar from "boring-avatars";

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = useState<boolean>(false);
  const [isInSmallDesktop, setIsInSmallDesktop] = useState<boolean>();
  const { me, userExtendData } = useAppSelector((state) => state.authReducer);
  const [activeTab, setActiveTab] = useState<string>("Q&A");
  const dispatch = useAppDispatch();
  const { searchText, handleSearch } = useSearch();

  const menuItems = [
    {
      key: "my-posts",
      label: (
        <span
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            color: "white",
          }}
        >
          <PenIcon width={16} height={16} />
          My Posts
        </span>
      ),
      onClick: () => {
        if (me?._id || me?.id) {
          navigate(`/user/${me?._id || me?.id}`);
        }
      },
    },
    {
      key: "profile",
      label: (
        <span
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            color: "white",
          }}
        >
          <ProfileIcon width={16} height={16} />
          My Profile
        </span>
      ),
      onClick: () => navigate("/profile"),
    },
    {
      key: "logout",
      label: (
        <span
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            color: "white",
          }}
        >
          <LogoutIcon width={16} height={16} />
          Logout
        </span>
      ),
      onClick: async () => {
        await dispatch(logout());
      },
    },
  ];

  const features = [
    {
      key: "Home",
      name: "Home",
      icon: <HomeIcon width={22} height={22} />,
      func: () => {
        navigate("/", { state: { community: "GLOBAL" } });
      },
    },
    {
      key: "Chat",
      name: "Chat",
      icon: <RecentIcon width={22} height={22} />,
      func: () => {
        navigate("chat");
      },
    },
    {
      key: "Booking Hotel",
      name: "Booking Hotel",
      icon: <CalendarIcon width={22} height={22} />,
      func: () => {
        navigate("booking");
      },
    },
  ];

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  useEffect(() => {
    function checkWidth() {
      if (window.innerWidth < 1200) {
        setIsInSmallDesktop(true);
      } else {
        setIsInSmallDesktop(false);
      }
    }

    checkWidth();
    window.addEventListener("resize", checkWidth);
    return () => window.removeEventListener("resize", checkWidth);
  }, []);

  useEffect(() => {
    const path = location.pathname;
    if (path === "/") {
      setActiveTab("Home");
    } else if (
      path === "/chat" ||
      path === "/flight" ||
      path === "/hotel" ||
      path === "/merchant"
    ) {
      setActiveTab("Chat");
    } else if (path === "/booking") {
      setActiveTab("Booking Hotel");
    } else {
      setActiveTab("");
    }
  }, [location.pathname]);

  return (
    <HeaderBar>
      <Left>
        <Logo
          src={logo}
          height={64}
          onClick={() => {
            navigate("/", { state: { community: "GLOBAL" } });
            if (isInSmallDesktop) toggleSidebar();
          }}
        />
        {location.pathname === "/" && (
          <SearchInput
            placeholder="# Explore"
            value={searchText}
            onChange={(e) => handleSearch(e.target.value)}
          />
        )}
      </Left>

      <Center>
        {features.map((feature) => (
          <Tooltip title={feature.name}>
            <NavIcon
              key={feature.key}
              onClick={feature.func}
              active={activeTab === feature.key}
            >
              {feature.icon}
            </NavIcon>
          </Tooltip>
        ))}
      </Center>

      {userExtendData ? (
        <Right>
          {userExtendData?.avatarUrl ? (
            <img
              style={{
                width: 32,
                height: 32,
                cursor: "pointer",
                borderRadius: "50%",
                objectFit: "cover",
              }}
              src={userExtendData?.avatarUrl}
              onClick={() => navigate("/profile")}
            />
          ) : (
            <Avatar
              name={me?.firstName + " " + me?.lastName}
              variant="beam"
              size={32}
              onClick={() => navigate("/profile")}
            />
          )}
          <Username>
            {me?.firstName} {me?.lastName}
          </Username>
          <Dropdown
            menu={{
              items: menuItems,
              style: {
                background: "#1a1a1a",
              },
            }}
            placement="topRight"
            trigger={["click"]}
          >
            <ArrowDownIcon style={{ cursor: "pointer" }} width={20} />
          </Dropdown>
        </Right>
      ) : (
        <Right>
          <LoginButton onClick={() => navigate("/login")}>Login</LoginButton>
        </Right>
      )}
    </HeaderBar>
  );
};

const HeaderBar = styled.header`
  width: 100%;
  background: #1a1a1a;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 2rem;
  box-sizing: border-box;
  height: 80px;
`;

const Left = styled.div`
  display: flex;
  align-items: center;
  gap: 1.25rem;
  width: 300px;
`;

const Logo = styled.img`
  height: 28px;
  width: auto;
  cursor: pointer;
`;

const SearchInput = styled.input`
  background: #222222;
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 0.5rem 1rem;
  font-size: 1rem;
  min-width: 180px;
  outline: none;
`;

const Center = styled.div`
  display: flex;
  align-items: center;
  gap: 2.5rem;
`;

const NavIcon = styled.div<{ active?: boolean; color?: string }>`
  background: none;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  cursor: pointer;
  color: ${({ color }) => color || "#fff"};
  position: relative;

  svg {
    width: 24px;
    height: 24px;
    display: block;
  }
  ${({ active }) =>
    active &&
    `
      &::after {
        content: '';
        position: absolute;
        left: 50%;
        transform: translateX(-50%);
        bottom: -12px;
        width: 8px;
        height: 8px;
        background: #FF8E53;
        border-radius: 50%;
      }
    `}
`;

const Right = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 300px;
  justify-content: flex-end;
`;

const AvatarImg = styled.img`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  object-fit: cover;
  background: #333;
`;

const Username = styled.span`
  color: #fff;
  font-weight: 500;
  font-size: 1rem;
  margin: 0 0.5rem;
`;

const ArrowDown = styled.img`
  width: 18px;
  height: 18px;
`;

const LoginButton = styled.p`
  color: #fff;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  margin: 0;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

export default Header;
